import {
  DataCard,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
  ButtonDS,
  ToastContainerDS,
  triggerToast,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  getSepaDirectDebitListAsync,
  selectGetSepaDirectDebitList,
  selectGetSepaDirectDebitListStatus,
  selectUpdateReturnedRefundDateStatus,
  companiesSepaDirectDebitSlice,
} from "./sepaDirectDebitSlice";
import { SepaDirectDebitReturnedDisplayed } from "./sepaDirectDebitApi";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { UpdateReturnedRefundDateModal } from "./modals/updateReturnedRefundDateModal";
import { formatDateWithHourMinute } from "../../lib/materialUiFormats";

export const SepaDirectDebitReturnedTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const sepaDirectDebitList = useAppSelector(selectGetSepaDirectDebitList);
  const sepaDirectDebitListStatus = useAppSelector(
    selectGetSepaDirectDebitListStatus,
  );
  const updateReturnedRefundDateStatus = useAppSelector(
    selectUpdateReturnedRefundDateStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    SepaDirectDebitReturnedDisplayed[] | null
  >(null);

  const [companyToHandleReturnedDate, setCompanyToHandleReturnedDate] =
    useState<SepaDirectDebitReturnedDisplayed | null>(null);

  const pageName = "sepaDirectDebitReturned";
  const apiRef = useGridApiRef();
  const key = "sepa-direct-debit-returned-grid";

  const aggregation = {
    model: {
      company: "size",
      amount: "avg",
      sepaDirectDebitStatus: "size",
      transferStatus: "size",
      triggerType: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "sepaDirectDebitStatus",
        "transferStatus",
      ] as (keyof SepaDirectDebitReturnedDisplayed)[],
    [],
  );

  const findKeysToTranslate = useCallback(
    (
      key: keyof SepaDirectDebitReturnedDisplayed,
      value: string | number | null,
    ) => {
      if (!value) return value;
      switch (key) {
        case "sepaDirectDebitStatus":
          return t(`sepaDirectDebitReturned.sepaDirectDebitStatus.${value}`);
        case "transferStatus":
          return t(`sepaDirectDebitReturned.transferStatus.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const sepaDirectDebitListDisplayed: SepaDirectDebitReturnedDisplayed[] =
    useMemo(() => {
      const filteredList = sepaDirectDebitList.filter(
        (item) =>
          item.sepaDirectDebitStatus === "RETURNED" &&
          item.transferStatus === "CONFIRMED",
      );

      const rowsWithId = filteredList.map((av) => {
        let newDict: SepaDirectDebitReturnedDisplayed & {
          id: string;
        } = {
          ...av,
          id: av.uuid,
        };
        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(
              key as keyof SepaDirectDebitReturnedDisplayed,
            )
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof SepaDirectDebitReturnedDisplayed,
                    value as string,
                  )
                : ("" as string),
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [sepaDirectDebitList, keysToTranslate, findKeysToTranslate]);

  const searchBarProps = {
    values: sepaDirectDebitListDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  useEffect(() => {
    if (sepaDirectDebitListStatus === "idle") {
      void dispatch(getSepaDirectDebitListAsync());
    }
  }, [sepaDirectDebitListStatus, dispatch]);

  useEffect(() => {
    if (
      sepaDirectDebitListDisplayed &&
      sepaDirectDebitListStatus === "success"
    ) {
      setListDisplayed(sepaDirectDebitListDisplayed);
    }
  }, [sepaDirectDebitListStatus, sepaDirectDebitListDisplayed]);

  useEffect(() => {
    if (updateReturnedRefundDateStatus === "success") {
      triggerToast(
        t("sepaDirectDebitReturned.updateRefundDate.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateReturnedRefundDateStatus(),
      );
    } else if (updateReturnedRefundDateStatus === "failed") {
      triggerToast(
        t("sepaDirectDebitReturned.updateRefundDate.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateReturnedRefundDateStatus(),
      );
    }
  }, [updateReturnedRefundDateStatus, t, dispatch]);

  const columnsDisplayed: {
    [index in keyof SepaDirectDebitReturnedDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      uuid: {
        type: "string",
      },
      companyName: {
        type: "string",
      },
      amount: {
        type: "number",
      },
      sepaDirectDebitStatus: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      sepaDirectDebitRequestedDate: {
        type: "string",
        valueFormatter: formatDateWithHourMinute,
      },
      sepaDirectDebitReceivedDate: {
        type: "string",
        valueFormatter: formatDateWithHourMinute,
      },
      transferStatus: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      transferRequestedDate: {
        type: "string",
        valueFormatter: formatDateWithHourMinute,
      },
      returnedRefundDate: {
        type: "string",
        renderCell: (params) => {
          if (params.id == GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
            return <></>;
          }
          return params.value ? (
            <StyledCenterCell>
              <TextCapitalized>
                {formatDateWithHourMinute(params.value as string)}
              </TextCapitalized>
            </StyledCenterCell>
          ) : (
            <StyledCenterCell>
              <ButtonDS
                text={t("sepaDirectDebitReturned.setRefundDate")}
                format="hug"
                buttonType="primary"
                sizeButton="S"
                onClick={() => {
                  setCompanyToHandleReturnedDate(
                    params.row as SepaDirectDebitReturnedDisplayed,
                  );
                }}
              />
            </StyledCenterCell>
          );
        },
      },
    };
  }, [t]);

  if (params.tab !== "sepaDirectDebitReturned") {
    return;
  }

  if (sepaDirectDebitListStatus === "processing" || !listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <StyledContainer>
      <ToastContainerDS />
      <Spacer y={1} />
      <StyledDiv>
        <DataCard
          title={t("sepaDirectDebitReturned.regularization.title")}
          content={
            <>
              <TextCapitalized>
                {t("sepaDirectDebitReturned.regularization.content")}
              </TextCapitalized>
              <Spacer y={1} />
              <Link
                to={`${import.meta.env["VITE_MEMOBANK_BASE_URL"]}/outgoing-collections/all/history`}
              >
                {t("sepaDirectDebitReturned.regularization.link")}
              </Link>
              <Spacer y={1} />
            </>
          }
        />
        <Spacer y={1} />
      </StyledDiv>
      <Spacer y={1} />
      <MaterialUiTable<
        keyof SepaDirectDebitReturnedDisplayed,
        SepaDirectDebitReturnedDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      <UpdateReturnedRefundDateModal
        onClose={() => setCompanyToHandleReturnedDate(null)}
        selectedRow={companyToHandleReturnedDate}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StyledDiv = styled.div`
    display: flex;
    ${typographies["Header/H3"]}]};
`;
const StyledCenterCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
